import { useEffect, useSyncExternalStore } from "react";
import type { User, Chat, Message, Settings } from "@glass/shared";

interface State {
  ready: boolean;
  user: User | null;
  settings: Settings | null;
  chats: Chat[];
  activeChatId: string | null;
  messagesByChat: Record<string, Message[]>;
  presenceByUser: Record<string, { online: boolean; last_seen: string | null; typing: { chat_id: string; until: number } | null }>;
}

const initial: State = {
  ready: false,
  user: null,
  settings: null,
  chats: [],
  activeChatId: null,
  messagesByChat: {},
  presenceByUser: {},
};

const listeners = new Set<() => void>();
let state: State = initial;

export const store = {
  get(): State { return state; },
  set(patch: Partial<State> | ((s: State) => Partial<State>)) {
    const next = typeof patch === "function" ? patch(state) : patch;
    state = { ...state, ...next };
    listeners.forEach((l) => l());
  },
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  upsertChat(chat: Chat) {
    state = {
      ...state,
      chats: [chat, ...state.chats.filter((c) => c.id !== chat.id)],
    };
    listeners.forEach((l) => l());
  },
  appendMessage(chatId: string, msg: Message) {
    const list = state.messagesByChat[chatId] || [];
    if (list.some((m) => m.id === msg.id)) return;
    state = {
      ...state,
      messagesByChat: { ...state.messagesByChat, [chatId]: [...list, msg] },
    };
    listeners.forEach((l) => l());
  },
  patchMessage(chatId: string, msgId: string, patch: Partial<Message>) {
    const list = state.messagesByChat[chatId] || [];
    state = {
      ...state,
      messagesByChat: {
        ...state.messagesByChat,
        [chatId]: list.map((m) => (m.id === msgId ? { ...m, ...patch } : m)),
      },
    };
    listeners.forEach((l) => l());
  },
  removeMessage(chatId: string, msgId: string) {
    const list = state.messagesByChat[chatId] || [];
    state = {
      ...state,
      messagesByChat: { ...state.messagesByChat, [chatId]: list.filter((m) => m.id !== msgId) },
    };
    listeners.forEach((l) => l());
  },
  prependMessages(chatId: string, msgs: Message[]) {
    const list = state.messagesByChat[chatId] || [];
    const existingIds = new Set(list.map((m) => m.id));
    const fresh = msgs.filter((m) => !existingIds.has(m.id));
    state = {
      ...state,
      messagesByChat: { ...state.messagesByChat, [chatId]: [...fresh, ...list] },
    };
    listeners.forEach((l) => l());
  },
};

export function useStore<T>(selector: (s: State) => T): T {
  const subscribe = (cb: () => void) => store.subscribe(cb);
  const get = () => selector(store.get());
  return useSyncExternalStore(subscribe, get, get);
}

export function useEffectOnce(fn: () => void | (() => void)) {
  useEffect(() => fn(), []); // eslint-disable-line react-hooks/exhaustive-deps
}
